import { useState, useEffect, useCallback } from 'react'
import NextApp from 'next/app'
import { useRouter } from 'next/router'
import NProgress from 'nprogress'

import '../public/css/react-material-class-layout.css'
import '../public/css/app.css'
import '../public/css/nprogress.css'
import '../public/css/twitter-styles.css'

import sentry from 'root/src/client/util/sentry'
import classNames from 'classnames'
import wrapper from 'root/src/client/logic/web'
import Navigation from 'root/src/client/web/base/Navigation'
import RenderModules from 'root/src/client/web/base/RenderModules'
import Footer from 'root/src/client/web/base/Footer'
import authDetermined from 'root/src/client/logic/app/actions/authDetermined'
import pick from 'ramda/src/pick'
import isEmpty from 'ramda/src/isEmpty'
import { buildCookie, getCookies } from 'root/src/client/util/cookies'
import getExpirationMinutes from 'root/src/client/logic/auth/util/getExpirationMinutes'
import refreshTokenFn from 'root/src/client/logic/auth/thunks/refreshToken'
import appConnector from 'root/src/client/logic/app/connectors/appConnector'
import cleanState from 'root/src/client/logic/app/actions/cleanState'
import setClientMetadata from 'root/src/client/logic/app/actions/setClientMetadata'
import { getInitialPropsRedirect } from 'root/src/client/util/redirects'
import RouteGuard from 'root/src/client/web/base/RouteGuard'
import HeadClient from 'root/src/client/web/base/HeadClient'
import ErrorBoundary from 'root/src/client/web/base/ErrorBoundary'
import TechIssueComponent from 'root/src/client/web/TechIssueComponent'
import Analytics from 'root/src/client/web/base/Analytics'
import FacebookPixelScript from 'root/src/client/web/scripts/FacebookPixelScript'
import ZohoLeadTrackingScript from 'root/src/client/web/scripts/ZohoLeadTrackingScript'
import TiktokPixelScript from 'root/src/client/web/scripts/TiktokPixelScript'
import clearCookies from 'root/src/client/util/clearCookies'

// prevents icon rendering before CSS is loaded
import '@fortawesome/fontawesome-svg-core/styles.css'

const styles = {
	content: {
		WebkitFlex: [[1, 0, 'auto']],
		flex: [[1, 0, 'auto']],
	},
	overflowContent: {
		overflow: 'hidden',
	},
}

NProgress.configure({ showSpinner: false })

// TODO @next. make some cleanup here after all. both server side and client side
const App = wrapper.withRedux(({
	metaData, testMediaBreakpoints, determineAuth,
	pageProps, Component, router: appRouter, classes = {},
	restrictionRedirect, routeRestriction, runModuleMountClient,
}) => {
	const [isInitRequestsDone, setIsInitRequestsDone] = useState(false)
	const router = useRouter()
	useEffect(() => {
		(async () => {
			const jssStyles = document.querySelector('#jss-server-side')
			if (jssStyles) {
				jssStyles.parentElement.removeChild(jssStyles)
			}
			testMediaBreakpoints()
			sentry()
			clearCookies()
			await determineAuth()
			setIsInitRequestsDone(true)
			const determineAuthLoop = () => {
				setTimeout(() => {
					determineAuth()
					determineAuthLoop()
				}, 1000 * 60 * 5) // we check auth every 5 minutes
			}
			determineAuthLoop()
		})()
	}, [])

	const handleStart = useCallback(() => {
		NProgress.start()
	}, [])

	const handleStop = useCallback(() => {
		NProgress.done()
	}, [])

	useEffect(() => {
		runModuleMountClient()
		router.events.on('routeChangeStart', handleStart)
		router.events.on('routeChangeComplete', handleStop)
		router.events.on('routeChangeError', handleStop)
		return () => {
			router.events.off('routeChangeStart', handleStart)
			router.events.off('routeChangeComplete', handleStop)
			router.events.off('routeChangeError', handleStop)
		}
	}, [router])

	if (appRouter.route === '/500') {
		return (
			<Component {...pageProps} />
		)
	}

	return (
		<ErrorBoundary FallbackComponent={TechIssueComponent}>
			<FacebookPixelScript />
			<ZohoLeadTrackingScript />
			<TiktokPixelScript />
			<Component {...pageProps} />
			<RouteGuard
				{...{ isInitRequestsDone, routeRestriction, restrictionRedirect, NProgress }}
			>
				<Analytics router={router} />
				<HeadClient metaData={metaData} />
				<div className={classNames(classes.content, classes.overflowContent, 'layout-column')}>
					<Navigation />
					<RenderModules />
					<Footer />
				</div>
			</RouteGuard>
		</ErrorBoundary>
	)
})

App.getInitialProps = wrapper.getInitialAppProps(store => async (appContext) => {
	// this happens on page changes - it's ran on client(it's how next works)
	if (!appContext.ctx.req) { return { } }

	getInitialPropsRedirect(appContext.ctx.res, appContext.router.asPath)

	// cleaning state is important step - otherwise we can leak
	// data between requests depending on lambda container state
	// probably worth to take a look at some point but nothing pressing atm
	store.dispatch(cleanState())
	const initialProps = await NextApp.getInitialProps(appContext)
	const { ctx: {
		req: { headers: {
			Cookie = '',
			'X-Forwarded-For': clientIp,
			'sec-ch-ua': userAgent,
			'sec-ch-ua-mobile': userAgentMobile,
			'sec-ch-ua-platform': platform,
		}, cookies: c },
		res,
		query,
	} } = appContext
	const { logout } = query
	// appContext.ctx.req.cookies works only on localhost
	store.dispatch(setClientMetadata({
		clientIp, userAgent, userAgentMobile, platform,
	}))

	const cookies = isEmpty(c) ? getCookies(Cookie) : c
	const auth = pick(['accessToken', 'email', 'refreshToken', 'userId', 'groups'], cookies || {})
	// TODO @next move this auth somewhere
	if (logout === 'true') {
		res.setHeader('Set-Cookie', [
			buildCookie('email', '', -1),
			buildCookie('refreshToken', '', -1),
			buildCookie('accessToken', '', -1),
			buildCookie('userId', '', -1),
			buildCookie('groups', '', -1),
		])
		store.dispatch(authDetermined())
	} else if (
		auth.refreshToken && (!auth.accessToken || getExpirationMinutes(auth.accessToken) <= 10)
	) {
		// TODO @next check if we need any logout on server
		// TODO @next find some way to refresh without email
		await store.dispatch(refreshTokenFn(
			auth,
			() => {},
			({
				accessToken, refreshToken, email, userId, accessTokenExp, refreshTokenExp, groups,
			}) => {
				res.setHeader('Set-Cookie', [
					buildCookie('email', email, refreshTokenExp),
					buildCookie('refreshToken', refreshToken, refreshTokenExp),
					buildCookie('accessToken', accessToken, accessTokenExp),
					buildCookie('userId', userId, accessTokenExp),
					buildCookie('groups', groups, accessTokenExp),
				])
			},
		))
	} else {
		store.dispatch(authDetermined(auth))
	}
	return { ...initialProps }
})

export default wrapper.withRedux(appConnector(App, styles, 'MuiApp'))
